import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DarkPage } from "@/components/app/PageHeader";
import { ShieldCheck, User } from "lucide-react";

const ROLE_STYLES = {
  owner: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  admin: "text-[#60A5FA] bg-[#60A5FA]/10 border-[#60A5FA]/20",
  member: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
  viewer: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
};

export default function Permissions() {
  const [capabilities, setCapabilities] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // `${user_id}:${capability}` while in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [capsRes, membersRes] = await Promise.all([
        api.get("/permissions/capabilities"),
        api.get("/permissions/overrides"),
      ]);
      setCapabilities(capsRes.data || []);
      setMembers(membersRes.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Only the workspace owner can manage permission overrides");
      } else {
        toast.error("Failed to load permissions");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (member, capability, nextValue) => {
    const key = `${member.user_id}:${capability}`;
    setSaving(key);
    try {
      const { data } = await api.put(`/permissions/overrides/${member.user_id}`, {
        overrides: { [capability]: nextValue },
      });
      setMembers(ms => ms.map(m => m.user_id === member.user_id
        ? { ...m, overrides: data.overrides, resolved: { ...m.resolved, [capability]: nextValue } }
        : m
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update permission");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="p-6 max-w-4xl mx-auto text-sm text-[#7d8590]">Loading…</div>;

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#7d8590]" /> Permissions</h1>
        <p className="text-sm text-[#7d8590] mt-1">
          Grant a specific capability to someone without changing their role, or revoke one someone would normally have. Overrides always win over the role default.
        </p>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-16 text-[#7d8590] text-sm">Only the workspace owner can view and manage permission overrides.</div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                <th className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium">Member</th>
                {capabilities.map(c => (
                  <th key={c.capability} className="text-center px-3 py-2.5 text-xs text-[#7d8590] font-medium" title={`Default: ${c.default_min_role}+`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.user_id} className="border-b border-[#21262d] last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-[#7d8590]" />
                      <div>
                        <div className="text-white">{m.name}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border capitalize ${ROLE_STYLES[m.role] || ROLE_STYLES.member}`}>{m.role}</span>
                      </div>
                    </div>
                  </td>
                  {capabilities.map(c => {
                    const key = `${m.user_id}:${c.capability}`;
                    const isOwner = m.role === "owner";
                    const resolved = !!m.resolved?.[c.capability];
                    const hasOverride = c.capability in (m.overrides || {});
                    return (
                      <td key={c.capability} className="px-3 py-3 text-center">
                        <button
                          disabled={isOwner || saving === key}
                          onClick={() => toggle(m, c.capability, !resolved)}
                          title={isOwner ? "The owner always has every capability" : hasOverride ? "Explicitly overridden" : "Following role default"}
                          className={`w-9 h-5 rounded-full relative transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${resolved ? "bg-[#00F5D4]" : "bg-[#30363d]"}`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#030712] transition-all ${resolved ? "left-[18px]" : "left-0.5"}`} />
                        </button>
                        {hasOverride && !isOwner && <div className="text-[9px] text-[#00F5D4] mt-1">override</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div></DarkPage>
  );
}
